import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Player } from "@lottiefiles/react-lottie-player";
import Icon1 from "../assets/icons/Icon";
import { faq_pages } from "./data";
import loadingAnimation from "../animations/loading.json";
import { User } from "../types/basicTypes";
import { colorFromTag, FaqItem } from "./Faq";
import { getDocDataById } from "../models/DataFunctions";
import SmallSpinner from "../loading/SmallSpinner";
import { SubscribeButton } from "./SubscribeButton";

interface LoginProps {
  userData: User | null;
  navigateWorkspace: (path: string) => void;
  handleLogout: any;
}

interface ContentSection {
  type: "text" | "table";
  content: string[];
}

interface FAQBlockProps {
  title: string;
  content: string;
  tag?: string;
  publication_date?: string;
}

interface TableRendererProps {
  tableLines: string[];
}

interface TextRendererProps {
  textLines: string[];
}

// Enhanced content formatter that handles tables, lists, and paragraphs
const formatContent = (content: string): ContentSection[] => {
  if (!content) return [];

  const lines = content.split("\n");
  const sections: ContentSection[] = [];
  let currentSection: string[] = [];
  let sectionType: "text" | "table" = "text";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines between sections
    if (line === "" && currentSection.length === 0) {
      continue;
    }

    // Detect table start (line with multiple | separators)
    const isTableRow = line.includes(" | ") && line.split(" | ").length > 1;
    const isTableSeparator = line.match(/^[-\s|]+$/);

    if (isTableRow || isTableSeparator) {
      // If we were building a text section, save it first
      if (sectionType === "text" && currentSection.length > 0) {
        sections.push({ type: "text", content: currentSection });
        currentSection = [];
      }

      if (isTableSeparator) {
        // Skip separator lines, but mark that we're in a table
        sectionType = "table";
        continue;
      }

      sectionType = "table";
      currentSection.push(line);
    } else {
      // If we were building a table, save it first
      if (sectionType === "table" && currentSection.length > 0) {
        sections.push({ type: "table", content: currentSection });
        currentSection = [];
      }

      sectionType = "text";
      if (line !== "") {
        currentSection.push(line);
      } else if (currentSection.length > 0) {
        // Empty line ends current text section
        sections.push({ type: "text", content: currentSection });
        currentSection = [];
      }
    }
  }

  // Add the last section
  if (currentSection.length > 0) {
    sections.push({ type: sectionType, content: currentSection });
  }

  return sections;
};

// Component to render a single table
const TableRenderer: React.FC<TableRendererProps> = ({ tableLines }) => {
  if (!tableLines || tableLines.length === 0) return null;

  const rows = tableLines.map((line) =>
    line.split("|").map((cell) => cell.trim())
  );

  if (rows.length === 0) return null;

  const headers = rows[0];
  const dataRows = rows.slice(1);

  return (
    <div className="w-full overflow-x-auto my-4">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                className="px-4 py-3 text-left text-h4 font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {dataRows.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-gray-50">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="px-4 py-3 text-h4 text-gray-700 border-b border-gray-100"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Component to render formatted text with bullet points
const TextRenderer: React.FC<TextRendererProps> = ({ textLines }) => {
  if (!textLines || textLines.length === 0) return null;

  return (
    <div className="my-2">
      {textLines.map((line, index) => {
        const trimmedLine = line.trim();

        // Handle bullet points
        if (trimmedLine.startsWith("•") || trimmedLine.startsWith("-")) {
          return (
            <div key={index} className="flex items-start my-1">
              <span className="text-gray-400 mr-3 mt-1 flex-shrink-0">•</span>
              <span className="text-gray-400">
                {trimmedLine.substring(1).trim()}
              </span>
            </div>
          );
        }

        // Handle table captions
        if (trimmedLine.startsWith("Tableau:")) {
          return (
            <div key={index} className="font-semibold text-gray-400 mt-4 mb-2">
              {trimmedLine}
            </div>
          );
        }

        // Regular paragraphs
        return (
          <div key={index}>
            {trimmedLine.split(".").map((part, i, arr) => (
              <p
                key={index + "-" + i}
                className="text-gray-500 my-2 leading-relaxed"
              >
                {part.trim().length > 1 ? part.trim() + ". " : ""}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// Enhanced FAQ Block component
const FAQBlock: React.FC<FAQBlockProps> = ({
  content,
  title,
  tag,
  publication_date = "22/05/2024",
}) => {
  const contentSections = formatContent(content);

  return (
    <div className="bg-wshadow_50 backdrop-blur-sm border-1 border-white rounded-2xl p-6 flex max-w-c700 w-full flex-col gap-4">
      {/* Header */}
      <div className="flex flex-row justify-between items-end">
        <div className="text-h4 text-neutral-400 mt-2">
          Publié le {publication_date}
        </div>
        <div
          className={`text-h4 font-medium py-1 px-2 mt-2 rounded-lg w-fit border-1 ${colorFromTag(
            tag || "default"
          )}`}
        >
          {tag}
        </div>
      </div>

      {/* Title */}
      <div className="text-h3 font-medium text-neutral-600 mb-4">{title}</div>

      {/* Content */}
      <div className="text-h3 font-medium text-neutral-400">
        {contentSections.map((section, index) => (
          <div key={index}>
            {section.type === "table" ? (
              <TableRenderer tableLines={section.content} />
            ) : (
              <TextRenderer textLines={section.content} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Updated main component
function FaqPage({ userData, navigateWorkspace, handleLogout }: LoginProps) {
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);

  const [faq_page, setFaqPage] = useState<FaqItem | null>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const handleFetchData = async () => {
    setLoading(true);
    let faq_page = (await getDocDataById("faq_pages", id)) as any as FaqItem;
    setFaqPage(faq_page);
    setLoading(false);
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  return (
    <div
      ref={mainScrollRef}
      className="h-screen overflow-auto bg-neutral-200 select-none"
    >
      <div className="h-fit flex z-10 pb-60 flex-row ">
        <div className="w-full h-fit flex relative justify-center items-end">
          <div className="w-full p-6 flex-col flex gap-4 h-fit md:items-center">
            <div className="font-extrabold text-hx4 md:text-hx6 text-neutral-600 mb-4">
              FAQ RE2020
            </div>

            <div
              onClick={() => navigateWorkspace("/faq")}
              className="bg-wshadow_50 cursor-pointer hover:bg-white duration-300 transition-all backdrop-blur-sm border-1 border-white rounded-2xl px-3 flex w-fit flex-col gap-2 py-2 pr-5"
            >
              <div className="flex flex-row gap-4">
                <Icon1 icon_name="left" fill="grey" size={20} />
                <div className="text-h3 font-medium text-neutral-600">
                  Retour
                </div>
              </div>
            </div>

            {/* FAQ Content */}

            {loading ? (
              <SmallSpinner></SmallSpinner>
            ) : (
              <>
                <div className="flex flex-col gap-2 items-start mt-4 select-text">
                  <div className="flex flex-row gap-4">
                    <FAQBlock
                      content={faq_page?.content || ""}
                      title={faq_page?.title || ""}
                      tag={faq_page?.tags[0]}
                      publication_date={
                        faq_page?.publication_date || "22/05/2024"
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {/* Bottom Back Button */}
            <div
              onClick={() => navigateWorkspace("/faq")}
              className="bg-wshadow_50 cursor-pointer hover:bg-white duration-300 transition-all backdrop-blur-sm border-1 border-white rounded-2xl px-3 flex w-fit flex-col gap-2 py-2 pr-5"
            >
              <div className="flex flex-row gap-4">
                <Icon1 icon_name="left" fill="grey" size={20} />
                <div className="text-h3 font-medium text-neutral-600">
                  Retour
                </div>
              </div>
            </div>
          </div>
          <SubscribeButton />
          <div
            onClick={() => {
              console.log("scroll to top");
              window.scrollTo({ top: 0, behavior: "smooth" });
              mainScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="fixed bg-wshadow_50 md:hidden rounded-full p-3 border-1 border-white bottom-40 right-8 z-20"
          >
            <Icon1 icon_name="up" size={22}></Icon1>
          </div>
        </div>
      </div>
    </div>
  );
}
export default FaqPage;
